import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../lib/currency';
import { ArrowDownLeft, Calendar, Clock } from 'lucide-react';

const AGING_BUCKETS = ['current', '1-30', '31-60', '61-90', '90+'] as const;

type AgingBucket = typeof AGING_BUCKETS[number];

type AgingData = Record<AgingBucket, number>;

interface LocalAgedBalances {
  rows: any[];
  buckets: AgingData;
  total: number;
}

const toNumber = (value: unknown): number => {
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const normalizeAgedBalances = (payload: any): LocalAgedBalances => {
  const report = payload?.data ?? payload ?? {};
  const sourceBuckets = report.buckets ?? report.aging ?? {};
  const buckets: AgingData = {
    current: toNumber(sourceBuckets.current),
    '1-30': toNumber(sourceBuckets['1-30'] ?? sourceBuckets.days1to30 ?? sourceBuckets.days_1_to_30),
    '31-60': toNumber(sourceBuckets['31-60'] ?? sourceBuckets.days31to60 ?? sourceBuckets.days_31_to_60),
    '61-90': toNumber(sourceBuckets['61-90'] ?? sourceBuckets.days61to90 ?? sourceBuckets.days_61_to_90),
    '90+': toNumber(sourceBuckets['90+'] ?? sourceBuckets.daysOver90 ?? sourceBuckets.days_over_90),
  };
  const rows = Array.isArray(report.rows) ? report.rows : Array.isArray(report.items) ? report.items : [];

  return {
    rows: rows.map((row: any) => ({
      ...row,
      id: row.id,
      number: row.number ?? row.documentNumber ?? row.document_number ?? '',
      date: row.date ?? row.documentDate ?? row.document_date ?? '',
      dueDate: row.dueDate ?? row.due_date ?? row.date ?? '',
      contactId: row.contactId ?? row.contact_id ?? '',
      contactName: row.contactName ?? row.contact_name ?? '',
      totalAmount: toNumber(row.totalAmount ?? row.total_amount),
      paidAmount: toNumber(row.paidAmount ?? row.paid_amount),
      outstanding: toNumber(row.outstanding ?? row.balance),
      ageDays: toNumber(row.ageDays ?? row.age_days),
    })),
    buckets,
    total: Number.isFinite(Number(report.total ?? report.totalAmount ?? report.total_amount))
      ? Number(report.total ?? report.totalAmount ?? report.total_amount)
      : Object.values(buckets).reduce((sum, amount) => sum + amount, 0),
  };
};

interface AgedReceivablesPayablesReportProps {
  activeReport: string | null;
  endDate: string;
  setEndDate: (date: string) => void;
  invoices: any[];
  bills: any[];
  calculateAging: (items: any[], type: 'Receivable' | 'Payable') => Record<string, number>;
  setActiveReport: (report: string | null) => void;
  currency?: string;
}

export const AgedReceivablesPayablesReport: React.FC<AgedReceivablesPayablesReportProps> = ({
  activeReport,
  endDate,
  setEndDate,
  invoices,
  bills,
  calculateAging,
  setActiveReport,
  currency = 'USD'
}) => {
  const isAR = activeReport?.includes('Receivables') ?? false;
  const reportKind = isAR ? 'receivable' : 'payable';
  const localToken = typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
  const localOrgId = typeof window !== 'undefined' ? localStorage.getItem('selected_org_id') : null;
  const [localAgedBalances, setLocalAgedBalances] = useState<LocalAgedBalances | null>(null);

  useEffect(() => {
    if (!localToken || !localOrgId) {
      setLocalAgedBalances(null);
      return;
    }

    const controller = new AbortController();
    setLocalAgedBalances(null);

    fetch(`/api/organizations/${encodeURIComponent(localOrgId)}/reports/aged-balances?kind=${reportKind}`, {
      headers: { Authorization: `Bearer ${localToken}` },
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok) throw new Error('Unable to load aged balances report');
        return response.json();
      })
      .then(payload => setLocalAgedBalances(normalizeAgedBalances(payload)))
      .catch((error: any) => {
        if (error.name !== 'AbortError') setLocalAgedBalances(null);
      });

    return () => controller.abort();
  }, [localToken, localOrgId, reportKind]);

  const firebaseAgingData = calculateAging(isAR ? invoices : bills, isAR ? 'Receivable' : 'Payable');
  const agingData = localAgedBalances?.buckets ?? firebaseAgingData;
  const total = localAgedBalances?.total ?? Object.values(agingData).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
          <Calendar className="w-4 h-4 opacity-50" />
          <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">As of</span>
          <input 
            type="date" 
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
          />
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">{activeReport}</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            As of {new Date(endDate).toLocaleDateString()}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-12">
          {Object.entries(agingData).map(([period, amount]) => (
            <div key={period} className="bg-white border border-[#141414] p-6 rounded-2xl text-center">
              <p className="text-[10px] uppercase font-bold opacity-40 mb-2">{period === 'current' ? 'Current' : `${period} Days`}</p>
              <p className="text-xl font-bold font-mono">{formatCurrency(amount, currency)}</p>
              <div className="w-full bg-[#141414]/5 h-1 rounded-full mt-4 overflow-hidden">
                <div 
                  className="bg-[#141414] h-full transition-all duration-1000" 
                  style={{ width: `${total > 0 ? (amount / total) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4 max-w-3xl mx-auto">
          <div className="flex justify-between items-center p-8 bg-[#141414] text-[#E4E3E0] rounded-[32px] shadow-xl">
            <div>
              <h3 className="text-sm uppercase tracking-widest font-bold opacity-50">Total {isAR ? 'Receivables' : 'Payables'}</h3>
              <p className="text-4xl font-bold tracking-tighter italic serif mt-2">
                {formatCurrency(total, currency)}
              </p>
            </div>
            <Clock className="w-12 h-12 opacity-50" />
          </div>
        </div>
      </div>
    </div>
  );
};
